import { useQuery } from '@tanstack/react-query';
import { useApi } from '@lex/api-client/react';
import type { HomeContentDto } from '@lex/contracts';

/**
 * Статический fallback контента главной. Используется, пока API недоступен
 * или ещё не ответил, чтобы страница всегда выглядела целостно.
 * Редактируемые разделы (FAQ, тарифы, шаги, юристы, настройки) приходят из API.
 */
export function useAvailability() {
  const api = useApi();
  return useQuery({
    queryKey: ['content', 'availability'],
    queryFn: () => api.content.availability(),
    staleTime: 60_000,
  });
}

export const FALLBACK_HOME: HomeContentDto = {
  settings: {
    navLinks: [
      { label: 'Призывникам', href: '/#for-whom' },
      { label: 'Военнослужащим', href: '/#for-whom' },
      { label: 'Как это работает', href: '/#how-it-works' },
      { label: 'Тарифы', href: '/#tariffs' },
      { label: 'Вопросы', href: '/#faq' },
    ],
    loginLabel: 'Войти',
    primaryCta: { label: 'Спросить юриста', href: '/chat' },
    phone: '+7 (800) 555-35-35',
    email: 'help@lex.ai',
    hoursText: 'Юристы на связи ежедневно 9:00–21:00 по Москве',
    workHoursStart: '09:00',
    workHoursEnd: '21:00',
    workDays: [1, 2, 3, 4, 5, 6, 7],
    nightMode: true,
    footerColumns: [
      {
        heading: 'Сервис',
        links: [
          { label: 'Призывникам', href: '/#for-whom' },
          { label: 'Военнослужащим', href: '/#for-whom' },
          { label: 'Как это работает', href: '/#how-it-works' },
          { label: 'Тарифы', href: '/#tariffs' },
        ],
      },
      {
        heading: 'Помощь',
        links: [
          { label: 'Частые вопросы', href: '/#faq' },
          { label: 'Мои обращения', href: '/cabinet' },
          { label: 'Telegram-канал', href: 'https://t.me/lexai' },
          { label: 'Контакты', href: 'mailto:help@lex.ai' },
        ],
      },
      {
        heading: 'Документы',
        links: [
          { label: 'Политика конфиденциальности', href: '/privacy' },
          { label: 'Условия сервиса', href: '/terms' },
          { label: 'Обработка персональных данных', href: '/consent' },
        ],
      },
    ],
    footerNoteTitle: 'Что с вашими документами',
    footerNoteText:
      'Перед загрузкой закройте ФИО, адрес и номера документов\nДокументы видят только юрист и сотрудники, которые ведут ваш вопрос\nПо вашему запросу мы удалим их из обращения',
    copyright: '© 2026 LEX AI',
    disclaimer:
      'Материалы сайта носят информационный характер, не являются юридической или медицинской консультацией и не заменяют очную помощь по конкретной ситуации.',
  },
  faq: [
    {
      id: 'fallback-faq-1',
      question: 'Консультация действительно анонимна?',
      answer:
        'Да. Мы не запрашиваем лишние персональные данные, а переписка доступна только вам и вашему юристу.',
    },
    {
      id: 'fallback-faq-2',
      question: 'Сколько ждать ответа юриста?',
      answer:
        'В рабочее время — в среднем 15–30 минут. Если вопрос срочный, отметьте это в чате, и мы подключим свободного юриста быстрее.',
    },
    {
      id: 'fallback-faq-3',
      question: 'Можно ли прикрепить документы?',
      answer:
        'Да. В чат можно приложить фото, сканы или PDF — юрист изучит материалы перед ответом.',
    },
    {
      id: 'fallback-faq-4',
      question: 'Что делать, если ситуация срочная?',
      answer:
        'Напишите в чат и укажите, что вопрос срочный. Мы постараемся подключить юриста вне очереди.',
    },
    {
      id: 'fallback-faq-5',
      question: 'Вы помогаете в суде?',
      answer:
        'Мы готовим документы, жалобы и заявления, а также сопровождаем вас на каждом этапе. Представительство в суде обсуждается отдельно.',
    },
  ],
  tariffs: [
    {
      id: 'fallback-tariff-once',
      key: 'once',
      name: 'Разовый',
      description: 'Один вопрос — один развёрнутый ответ',
      price: 990,
      currency: '₽',
      period: 'за вопрос',
      badge: null,
      features: [
        'Ответ юриста в чате',
        'Разбор вашей ситуации',
        'План действий',
        'Ответ в течение 30 минут',
      ],
      highlighted: false,
      ctaLabel: 'Задать вопрос',
      ctaHref: '/chat',
    },
    {
      id: 'fallback-tariff-week',
      key: 'week',
      name: 'Неделя',
      description: 'Сопровождение по вашему вопросу 7 дней',
      price: 2990,
      currency: '₽',
      period: '7 дней',
      badge: 'Популярный',
      features: [
        'Неограниченные вопросы',
        'Проверка документов',
        'Подготовка заявлений',
        'Связь с юристом 7 дней',
      ],
      highlighted: true,
      ctaLabel: 'Выбрать тариф',
      ctaHref: '/chat',
    },
    {
      id: 'fallback-tariff-month',
      key: 'month',
      name: 'Месяц',
      description: 'Полное сопровождение на месяц',
      price: 7990,
      currency: '₽',
      period: '30 дней',
      badge: null,
      features: [
        'Всё из тарифа «Неделя»',
        'Ведение переписки с госорганами',
        'Жалобы и обжалования',
        'Приоритетная связь',
      ],
      highlighted: false,
      ctaLabel: 'Выбрать тариф',
      ctaHref: '/chat',
    },
  ],
  steps: [
    {
      id: 'fallback-step-1',
      number: '1',
      title: 'Опишите ситуацию',
      body: 'Расскажите, что произошло, в свободной форме. Прикрепите документы, если они есть.',
    },
    {
      id: 'fallback-step-2',
      number: '2',
      title: 'Юрист изучит материалы',
      body: 'Профильный юрист проверит обстоятельства, документы и применимые нормы права.',
    },
    {
      id: 'fallback-step-3',
      number: '3',
      title: 'Получите план действий',
      body: 'Вы получите понятный план, шаблоны заявлений и поддержку на каждом шаге.',
    },
  ],
  lawyers: [
    {
      id: 'fallback-lawyer-1',
      name: 'Анна Смирнова',
      title: 'Военный юрист',
      photoUrl: null,
      specializations: ['Призыв', 'Отсрочки', 'Обжалование'],
      bio: null,
      online: true,
    },
    {
      id: 'fallback-lawyer-2',
      name: 'Дмитрий Ковалёв',
      title: 'Юрист по военному праву',
      photoUrl: null,
      specializations: ['Выплаты', 'Льготы', 'Контракт'],
      bio: null,
      online: true,
    },
    {
      id: 'fallback-lawyer-3',
      name: 'Елена Морозова',
      title: 'Юрист по социальным вопросам',
      photoUrl: null,
      specializations: ['Медицина', 'Семья', 'Документы'],
      bio: null,
      online: false,
    },
  ],
};

export function useHomeContent() {
  const api = useApi();
  return useQuery({
    queryKey: ['content', 'home'],
    queryFn: () => api.content.home(),
    staleTime: 60_000,
  });
}
